import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { MagnifyingGlass, Star, CurrencyDollar, CurrencyBtc, DotsSixVertical } from "@phosphor-icons/react";
import {
  DndContext, closestCenter, PointerSensor, TouchSensor, KeyboardSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove, sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { AppHeader } from "../components/AppHeader";
import { AssetCard } from "../components/AssetCard";
import { ChangeIndicator } from "../components/ChangeIndicator";
import { CurrencyConverter } from "../components/CurrencyConverter";
import { DataStatus } from "../components/DataStatus";
import { Disclaimer } from "../components/Disclaimer";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Skeleton } from "../components/ui/skeleton";
import api from "../lib/api";
import { timeAgo, fmtNum, fmtMoney } from "../lib/format";
import { useAuth } from "../context/AuthContext";

function StaleBanner({ stale, updated }) {
  if (stale) {
    return (
      <div className="border border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400 text-xs px-3 py-2 mb-4" data-testid="stale-banner">
        Last updated {timeAgo(updated)}. Market data provider is temporarily unavailable.
      </div>
    );
  }
  return null;
}

function SectionLabel({ children, icon: Icon }) {
  return (
    <div className="flex items-center gap-2 mb-4">
      {Icon && <Icon size={18} weight="bold" className="text-primary" />}
      <h2 className="text-xs font-bold tracking-[0.2em] uppercase text-muted-foreground">{children}</h2>
    </div>
  );
}

function CardGrid({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">{children}</div>;
}

function LoadingGrid() {
  return (
    <CardGrid>
      {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-[150px] rounded-none" />)}
    </CardGrid>
  );
}

function SortableRow({ w, base, nav }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: w.id });
  const style = { transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 20 : undefined };
  return (
    <div ref={setNodeRef} style={style} data-testid={`watchlist-item-${w.symbol.toLowerCase()}`}
      className={`flex items-center justify-between px-2 sm:px-4 py-3 bg-card ${isDragging ? "shadow-lg border border-primary/40" : ""}`}>
      <div className="flex items-center gap-2 min-w-0">
        <button {...attributes} {...listeners} data-testid={`drag-${w.symbol.toLowerCase()}`}
          className="cursor-grab active:cursor-grabbing text-muted-foreground/60 hover:text-foreground touch-none p-1" aria-label="Drag to reorder">
          <DotsSixVertical size={18} weight="bold" />
        </button>
        <button onClick={() => nav(w.asset_type === "crypto" ? `/crypto/${w.coin_id}` : `/currency/${w.symbol}`)}
          className="flex items-center gap-3 text-left min-w-0">
          <Star size={16} weight="fill" className="text-primary shrink-0" />
          <span className="font-heading font-bold">{w.symbol}</span>
          <span className="text-xs text-muted-foreground hidden sm:inline truncate">{w.name}</span>
        </button>
      </div>
      <div className="flex items-center gap-4">
        <span className="font-mono text-sm font-semibold">
          {w.asset_type === "crypto" ? fmtMoney(w.value, base) : fmtNum(w.value)}
        </span>
        <ChangeIndicator value={w.change} />
      </div>
    </div>
  );
}

function Watchlist() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const base = user?.base_currency || "USD";
  const { data: resp, isLoading, isError } = useQuery({
    queryKey: ["watchlist", base],
    queryFn: async () => (await api.get("/watchlist", { params: { base } })).data,
  });
  const data = resp?.watchlist;
  const [items, setItems] = useState([]);
  useEffect(() => { if (data) setItems(data); }, [data]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((i) => i.id === active.id);
    const newIndex = items.findIndex((i) => i.id === over.id);
    const next = arrayMove(items, oldIndex, newIndex);
    setItems(next);
    try {
      await api.post("/favorites/reorder", { ids: next.map((i) => i.id) });
      qc.invalidateQueries({ queryKey: ["favorites"] });
    } catch { setItems(data); }
  };

  // combined freshness across whichever asset types the user actually tracks (no extra fetch)
  const ts = [resp?.currency_updated_at, resp?.crypto_updated_at].filter(Boolean);
  const oldest = ts.length ? ts.reduce((a, b) => (new Date(a) < new Date(b) ? a : b)) : null;
  const stale = !!(resp?.currency_stale || resp?.crypto_stale);

  if (isLoading) return <Skeleton className="h-24 rounded-none mb-8" />;
  if (!items || items.length === 0) {
    return (
      <div className="border border-dashed border-border p-6 text-center mb-8" data-testid="watchlist-empty">
        <Star size={22} className="mx-auto text-muted-foreground mb-2" />
        <p className="text-sm text-muted-foreground">Your favorites list is empty. Tap the star on any asset to track it here.</p>
      </div>
    );
  }
  return (
    <div className="mb-8">
      <div className="flex items-center justify-end mb-2">
        <DataStatus updatedAt={oldest} stale={stale} error={isError} testid="watchlist-status" />
      </div>
      <div className="border border-border bg-card" data-testid="watchlist">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
            <div className="divide-y divide-border">
              {items.map((w) => <SortableRow key={w.id} w={w} base={base} nav={nav} />)}
            </div>
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
}

function CurrenciesTab() {
  const [q, setQ] = useState("");
  const [reversed, setReversed] = useState(false);
  const { user } = useAuth();
  const base = user?.base_currency || "USD";
  const featured = useQuery({
    queryKey: ["currencies-featured", base],
    queryFn: async () => (await api.get("/currencies/featured", { params: { base } })).data,
  });
  const search = useQuery({
    queryKey: ["currencies-search", q, base],
    queryFn: async () => (await api.get("/currencies/search", { params: { q, base } })).data,
    enabled: q.trim().length > 0,
  });

  const mapCur = (c) => ({ type: "currency", symbol: c.code, name: c.name, price: c.rate, change: c.change, sparkline: c.sparkline });

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
        <div className="relative flex-1">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="currency-search"
            placeholder="Search currency by code, name or country (e.g. EUR, Japan)…"
            className="pl-10 h-11 rounded-none" />
        </div>
        <label className="flex items-center gap-2 border border-border bg-card h-11 px-3 shrink-0 cursor-pointer select-none" title="Flip rate direction">
          <Switch checked={reversed} onCheckedChange={setReversed} data-testid="currency-reverse-switch" />
          <span className="text-xs font-medium text-muted-foreground whitespace-nowrap font-mono">
            {reversed ? `1 CUR → ${base}` : `1 ${base} → CUR`}
          </span>
        </label>
      </div>

      {q.trim() ? (
        search.isLoading ? <LoadingGrid /> : (
          <>
            <SectionLabel>Search Results</SectionLabel>
            {search.data?.currencies?.length ? (
              <CardGrid>{search.data.currencies.map((c, i) => <AssetCard key={c.code} asset={mapCur(c)} index={i} reversed={reversed} />)}</CardGrid>
            ) : <p className="text-sm text-muted-foreground" data-testid="no-currency-results">No currencies match "{q}".</p>}
          </>
        )
      ) : (
        <>
          <StaleBanner stale={featured.data?.stale} updated={featured.data?.updated_at} />
          <div className="flex items-center justify-between mb-4">
            <SectionLabel icon={CurrencyDollar}>Featured Currencies</SectionLabel>
            <DataStatus updatedAt={featured.data?.updated_at} stale={featured.data?.stale} error={featured.isError} testid="currency-data-status" />
          </div>
          {featured.isLoading ? <LoadingGrid /> : (
            <CardGrid>{featured.data?.currencies?.map((c, i) => <AssetCard key={c.code} asset={mapCur(c)} index={i} reversed={reversed} />)}</CardGrid>
          )}

          <div className="mt-8 max-w-md">
            <CurrencyConverter defaultTo={featured.data?.currencies?.[0]?.code || "IQD"} />
          </div>
        </>
      )}
    </div>
  );
}

function CryptoTab() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [dq, setDq] = useState("");
  const { user } = useAuth();
  const base = user?.base_currency || "USD";
  // debounce so we never fire a request per keystroke (local catalog resolves instantly anyway)
  useEffect(() => {
    const id = setTimeout(() => setDq(q.trim()), 350);
    return () => clearTimeout(id);
  }, [q]);
  const featured = useQuery({
    queryKey: ["crypto-featured", base],
    queryFn: async () => (await api.get("/crypto/featured", { params: { base } })).data,
  });
  const search = useQuery({
    queryKey: ["crypto-search", dq],
    queryFn: async () => (await api.get("/crypto/search", { params: { q: dq } })).data,
    enabled: dq.length > 0,
  });

  const mapCoin = (c) => ({ type: "crypto", symbol: c.symbol.toUpperCase(), name: c.name, price: c.current_price, change: c.price_change_percentage_24h, coinId: c.id, image: c.image, sparkline: c.sparkline });

  return (
    <div>
      <div className="relative mb-6">
        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="crypto-search"
          placeholder="Search cryptocurrency by symbol or name (e.g. BTC, Bitcoin)…"
          className="pl-10 h-11 rounded-none" />
      </div>

      {q.trim() ? (
        (search.isLoading || q.trim() !== dq) ? <LoadingGrid /> : (
          <>
            <SectionLabel>Search Results</SectionLabel>
            {search.data?.crypto?.length ? (
              <div className="border border-border bg-card divide-y divide-border" data-testid="crypto-search-results">
                {search.data.crypto.map((c) => (
                  <button key={c.id} onClick={() => nav(`/crypto/${c.id}`)} data-testid={`crypto-result-${c.symbol.toLowerCase()}`}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-accent transition-colors text-left">
                    {c.thumb && <img src={c.thumb} alt="" className="h-6 w-6 rounded-full" />}
                    <span className="font-heading font-bold">{c.symbol}</span>
                    <span className="text-sm text-muted-foreground">{c.name}</span>
                  </button>
                ))}
              </div>
            ) : <p className="text-sm text-muted-foreground" data-testid="no-crypto-results">No cryptocurrencies match "{q}".</p>}
          </>
        )
      ) : (
        <>
          <StaleBanner stale={featured.data?.stale} updated={featured.data?.updated_at} />
          <div className="flex items-center justify-between mb-4">
            <SectionLabel icon={CurrencyBtc}>Featured Crypto</SectionLabel>
            <DataStatus updatedAt={featured.data?.updated_at} stale={featured.data?.stale} error={featured.isError} testid="crypto-data-status" />
          </div>
          {featured.isLoading ? <LoadingGrid /> : (
            <CardGrid>{featured.data?.crypto?.map((c, i) => <AssetCard key={c.id} asset={mapCoin(c)} index={i} />)}</CardGrid>
          )}
        </>
      )}
    </div>
  );
}

export default function Dashboard() {
  const [tab, setTab] = useState("currencies");
  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <main className="mx-auto max-w-7xl px-4 sm:px-6 py-8">
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mb-1">Market Dashboard</h1>
        <p className="text-sm text-muted-foreground mb-8">Latest available currency rates &amp; cryptocurrency prices.</p>

        <SectionLabel icon={Star}>My Favorites</SectionLabel>
        <Watchlist />

        <div className="inline-flex border border-border mb-8" data-testid="main-tabs">
          <button onClick={() => setTab("currencies")} data-testid="tab-currencies"
            className={`px-6 py-2.5 text-sm font-bold uppercase tracking-wider transition-colors ${tab === "currencies" ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}>
            Currencies
          </button>
          <button onClick={() => setTab("crypto")} data-testid="tab-crypto"
            className={`px-6 py-2.5 text-sm font-bold uppercase tracking-wider transition-colors border-l border-border ${tab === "crypto" ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}>
            Crypto
          </button>
        </div>

        {tab === "currencies" ? <CurrenciesTab /> : <CryptoTab />}
        <Disclaimer />
      </main>
    </div>
  );
}
